/**
 * Day/night cycle — pure sim, render-free (no three.js, no Math.random).
 *
 * timeOfDay is 0..1 with 0 = midnight, 0.5 = noon. Maps 1:1 to the planned
 * UE day/night subsystem: sun elevation drives both presentation
 * (render/daynight.ts) and gameplay (nightFactor shrinks vision ranges in
 * src/sim/vision.ts and gates torch visuals; fighter torch bubbles fold
 * into the fighter cluster viewshed — see vision.ts).
 */

// --- Tunables ---
/** Sim-seconds per full day/night cycle (8 game-minutes per day). */
export const DAY_LENGTH_S = 480;
/** Sun-elevation band over which nightFactor transitions 1 -> 0. */
export const NIGHT_ELEVATION = -0.18; // below this: deep night
export const DAY_ELEVATION = 0.08; // above this: full day
/** Morning start time (matches the pre-cycle prototype's daylight look). */
export const MORNING_START = 0.32;

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/** Smoothstep on [a, b]; a < b. */
function smoothstep(a: number, b: number, x: number): number {
  const t = clamp01((x - a) / (b - a));
  return t * t * (3 - 2 * t);
}

export class DayNightCycle {
  /** 0..1, 0 = midnight, 0.5 = noon. */
  timeOfDay: number;

  constructor(startTime: number = MORNING_START) {
    this.timeOfDay = ((startTime % 1) + 1) % 1;
  }

  tick(dt: number): void {
    this.timeOfDay = (this.timeOfDay + dt / DAY_LENGTH_S) % 1;
  }

  /** Sun elevation -1..1 (1 = directly overhead, -1 = directly below). */
  elevation(): number {
    return Math.sin(2 * Math.PI * (this.timeOfDay - 0.25));
  }

  /**
   * 0 in day, 1 in deep night, smooth across dawn/dusk. The transition
   * band spans ~1h of game time on each side (see NIGHT/DAY_ELEVATION).
   */
  nightFactor(): number {
    return 1 - smoothstep(NIGHT_ELEVATION, DAY_ELEVATION, this.elevation());
  }

  /** e.g. "☀ 13:24" / "☾ 02:10". */
  clockLabel(): string {
    const hours = Math.floor(this.timeOfDay * 24);
    const minutes = Math.floor((this.timeOfDay * 24 * 60) % 60);
    const pad = (n: number): string => String(n).padStart(2, "0");
    const icon = this.nightFactor() > 0.5 ? "☾" : "☀";
    return `${icon} ${pad(hours)}:${pad(minutes)}`;
  }

  serialize(): number {
    return this.timeOfDay;
  }

  restore(t: number): void {
    this.timeOfDay = ((t % 1) + 1) % 1;
  }
}
